// Toast presentacional — anuncia por role/aria-live conforme o tom. A fila, os
// timers e o ciclo de vida ficam no ToastProvider; aqui refletimos o estado via
// data-state pra CSS animar e expomos os hooks de hover/foco que pausam lá.
//
// 🔑 Este componente é usado em DOIS regimes, e é por isso que ele não sabe nada
// sobre a pilha:
//
//   1. dentro da região do provider, envolvido por um `.slot` posicionado;
//   2. **solto no fluxo da página**, como banner inline — 10 telas fazem isso
//      (LoginPage, NetworkTeamTab, WhatsappManualToken, …).
//
// Posicionamento, escala e z-index moram no `.slot` do ToastProvider. Se
// vazassem pra cá, todo uso inline viraria elemento absoluto e sairia do fluxo.
//
// A estrutura interna tem duas camadas com papéis distintos:
//   .toast  entrada/saída (keyframes) + altura interpolada
//   .inner  o conteúdo, e o que o ResizeObserver mede
import {
  useCallback,
  useEffect,
  useRef,
  type ComponentType,
  type CSSProperties,
  type ReactNode,
} from 'react';
import styles from './Toast.module.css';
import { cn } from '@/lib/utils/cn';
import { Button } from '@/components/ui/Button';
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  InfoIcon,
  type IconProps,
} from '@/components/icons';
import type { ToastTone } from '@/config/toast';

export type { ToastTone };
/** 'open' = visível/entrando · 'leaving' = tocando a animação de saída. */
export type ToastState = 'open' | 'leaving';

/** Ação opcional dentro do toast. Hoje o caso de uso é o "Desfazer" (RF-06). */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastProps {
  tone?: ToastTone;
  title: string;
  description?: ReactNode;
  state?: ToastState;
  action?: ToastAction;
  onDismiss?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;

  // --- Só o provider preenche daqui pra baixo. Uso inline ignora tudo. ------

  /** Identifica o toast ao reportar altura. */
  id?: string;
  /** Reporta a altura do conteúdo pro provider calcular a geometria da pilha. */
  onHeightChange?: (id: string, height: number) => void;
  /**
   * Altura medida, devolvida pelo provider. Aplicada no `.toast` (não no
   * `.inner`, que é quem mede) pra que a mudança de conteúdo num `update()` —
   * loading → success — cresça ou encolha com transição em vez de saltar. É a
   * mesma medida que alimenta a pilha: uma fonte só.
   */
  height?: number;
  /** Pilha aberta: pausa a barra de contagem junto com o timer real. */
  expanded?: boolean;
  /** É o toast da frente (o mais novo)? Colapsada, só ele mostra conteúdo. */
  front?: boolean;
  /**
   * Duração da barra de contagem regressiva. Só aparece junto de `action`: a
   * barra responde "quanto tempo tenho pra desfazer", pergunta que não existe
   * num toast sem ação.
   */
  countdownMs?: number;
  /**
   * Muda quando o provider reagenda o timer. É `key` da barra: React remonta o
   * nó e a animação CSS recomeça. Sem isto, um `update()` com duração nova
   * deixaria a barra correndo no ritmo antigo — mostrando um prazo que não é o
   * que o `setTimeout` vai cumprir.
   */
  countdownKey?: number;
}

const toneClass: Record<ToastTone, string> = {
  info: styles.info,
  success: styles.success,
  warning: styles.warning,
  danger: styles.danger,
  loading: styles.loading,
};

// `loading` não tem ícone estático — cai no spinner.
const toneIcon: Record<ToastTone, ComponentType<IconProps> | null> = {
  info: InfoIcon,
  success: CheckCircleIcon,
  warning: AlertTriangleIcon,
  danger: AlertCircleIcon,
  loading: null,
};

export function Toast({
  tone = 'info',
  title,
  description,
  state = 'open',
  action,
  onDismiss,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  id,
  onHeightChange,
  height,
  expanded = false,
  front = true,
  countdownMs,
  countdownKey = 0,
}: ToastProps) {
  // Erro e aviso interrompem o leitor de tela; o resto espera a vez.
  const assertive = tone === 'danger' || tone === 'warning';
  const Icon = toneIcon[tone];
  const innerRef = useRef<HTMLDivElement>(null);

  // Mede o CONTEÚDO (.inner) e devolve pro provider, que aplica a altura no pai
  // (.toast). Observar o mesmo elemento que recebe a altura fecharia um laço:
  // escrever a altura dispara o observer, que reporta, que escreve de novo.
  const measure = useCallback(() => {
    const el = innerRef.current;
    if (!el || !id || !onHeightChange) return;
    onHeightChange(id, el.getBoundingClientRect().height);
  }, [id, onHeightChange]);

  // 🔑 A cada render, sem deps. A causa mais comum de mudança de altura é a
  // troca de conteúdo num `update()` (loading → success) — e essa o React já
  // conhece. Delegá-la ao observer custaria um frame de atraso justamente na
  // transição que se quer suave, e quebraria de vez onde o navegador não está
  // pintando (aba em segundo plano não roda `requestAnimationFrame`, e sem
  // frame o ResizeObserver nunca entrega).
  // Converge: reportar a mesma altura devolve o state anterior e não re-renderiza.
  useEffect(measure);

  // O observer cobre o que muda FORA do React: fonte que carrega depois do
  // primeiro paint, largura da janela que força uma quebra de linha a mais.
  useEffect(() => {
    const el = innerRef.current;
    // jsdom não implementa ResizeObserver; a medida por render já cobre o
    // essencial, então a ausência dele degrada em vez de quebrar.
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  const showCountdown = !!action && !!countdownMs && countdownMs > 0;

  const toastStyle: CSSProperties = {};
  if (height) (toastStyle as Record<string, string>)['--toast-height'] = `${height}px`;

  return (
    <div
      role={assertive ? 'alert' : 'status'}
      aria-live={assertive ? 'assertive' : 'polite'}
      data-state={state}
      data-tone={tone}
      data-expanded={expanded ? '' : undefined}
      data-front={front ? '' : undefined}
      className={cn(styles.toast, toneClass[tone])}
      style={toastStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // Foco também pausa o timer: quem navega por teclado precisa de tempo pra
      // alcançar o "Desfazer" sem o toast sumir no meio do caminho (RF-09).
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <div className={styles.inner} ref={innerRef}>
        {/* Ícone é reforço REDUNDANTE do tom (RF-04/09): a cor sozinha não pode
            carregar o significado. Quem lê por leitor de tela recebe pelo texto.
            A `key` força remontagem quando o tom muda (loading → success), e é o
            que dá o fade de troca em vez do corte seco. */}
        {Icon ? (
          <Icon key={tone} className={styles.icon} size={18} aria-hidden="true" />
        ) : (
          <span key={tone} className={styles.spinner} aria-hidden="true" />
        )}
        <div className={styles.body}>
          <strong className={styles.title}>{title}</strong>
          {description && <span className={styles.description}>{description}</span>}
        </div>
        {/* Fora do `.body`: a ação fica à direita, entre o texto e o ×. A ordem no
            DOM é a mesma de antes (texto → ação → dispensar), então a navegação
            por teclado continua alcançando "Desfazer" antes de fechar. */}
        {action && (
          <Button variant="ghost" size="sm" className={styles.action} onClick={action.onClick}>
            {action.label}
          </Button>
        )}
        {onDismiss && (
          <button
            type="button"
            className={styles.dismiss}
            onClick={onDismiss}
            aria-label="Dispensar notificação"
          >
            ×
          </button>
        )}
      </div>
      {/* Fora do .inner de propósito: a barra é absoluta e NÃO entra na altura
          medida — se entrasse, cada barra empurraria a pilha em 3px. aria-hidden
          porque o prazo não pode ser reanunciado a cada frame; quem carrega essa
          informação pro leitor de tela é o rótulo do botão. */}
      {showCountdown && (
        <div
          key={countdownKey}
          className={styles.countdown}
          style={
            {
              '--toast-countdown': `${countdownMs}ms`,
              // Degraus de ~1s pro fallback de prefers-reduced-motion (ver o
              // CSS): a barra continua exata, só deixa de deslizar.
              '--toast-countdown-steps': Math.max(1, Math.round(countdownMs / 1000)),
            } as CSSProperties
          }
          aria-hidden="true"
        />
      )}
    </div>
  );
}
